import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ROLE_EDIT_SEO } from 'helpers/roles';
import FormLabel from '@material-ui/core/FormLabel';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import CardBody from 'components/Card/CardBody';
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import CardWrapper from 'components/Card/CardWrapper';
import { makeStyles } from '@material-ui/styles';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';

import TitleLetterCounter from './components/TitleLetterCounter';
import DescriptionLetterCounter from './components/DescriptionLetterCounter';

const useStyles = makeStyles({
  labelHorizontal: extendedFormsStyle.labelHorizontal,
  gridContainer: {
    marginTop: '20px',
  },
  gridItem: {
    marginLeft: '0',
  },
  uploadedWrapper: {
    marginLeft: '-15px',
  },
});

const SEOSettingsCms = ({
  section,
  state,
  setState,
  moduleConfiguration = true,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  // eslint-disable-next-line no-shadow -- FIXME
  const roles = useSelector(state => state.Auth.user.roles);

  const handleInputChange = event => {
    const newValue = event.target.value;

    if (moduleConfiguration) {
      setState(prevState => ({
        ...prevState,
        configuration: {
          ...prevState.configuration,
          [section]: {
            ...prevState.configuration[section],
            [event.target.name]: newValue,
          },
        },
      }));
    } else {
      setState(prev => ({
        ...prev,
        [event.target.name]: newValue,
      }));
    }
  };

  const handleToggle = () => {
    if (moduleConfiguration) {
      setState(prevState => ({
        ...prevState,
        configuration: {
          ...prevState.configuration,
          [section]: {
            ...prevState.configuration[section],
            indexing: !prevState.configuration[section]?.indexing,
          },
        },
      }));
    } else {
      setState(prev => ({
        ...prev,
        indexing: !prev?.indexing,
      }));
    }
  };

  const getImage = (stateName, data) => {
    if (moduleConfiguration) {
      setState(prevState => ({
        ...prevState,
        configuration: {
          ...prevState.configuration,
          [section]: {
            ...prevState.configuration[section],
            [stateName]: data?.['@id'] || null,
            [`${stateName}Url`]: data?.contentUrl || null,
          },
        },
      }));
    } else {
      setState(prev => ({
        ...prev,
        [stateName]: data?.['@id'] || null,
        [`${stateName}Url`]: data?.contentUrl || null,
      }));
    }
  };

  const removeImage = stateName => {
    if (moduleConfiguration) {
      setState(prevState => ({
        ...prevState,
        configuration: {
          ...prevState.configuration,
          [section]: {
            ...prevState.configuration[section],
            [stateName]: null,
            [`${stateName}Url`]: null,
          },
        },
      }));
    } else {
      setState(prev => ({
        ...prev,
        [stateName]: null,
        [`${stateName}Url`]: null,
      }));
    }
  };

  const isEditEnabled = roles ? roles.includes(ROLE_EDIT_SEO) : false;

  return (
    <>
      <CardWrapper title={t(`brandCfg.seoSection.${section}`)}>
        <CardBody>
          <GridContainer>
            <GridItem md={12}>
              <FormTextInput
                label={
                  <TitleLetterCounter
                    wordsCount={
                      moduleConfiguration
                        ? state?.configuration?.[section]?.marketingTitle
                            ?.length
                        : state?.marketingTitle?.length
                    }
                  />
                }
                name="marketingTitle"
                value={
                  moduleConfiguration
                    ? state?.configuration?.[section]?.marketingTitle
                    : state?.marketingTitle
                }
                handleChange={handleInputChange}
                inputSize={6}
                disabled={!isEditEnabled}
              />
              <FormTextInput
                label={
                  <DescriptionLetterCounter
                    wordsCount={
                      moduleConfiguration
                        ? state?.configuration?.[section]?.metaDescription
                            ?.length
                        : state?.metaDescription?.length
                    }
                  />
                }
                name="metaDescription"
                value={
                  moduleConfiguration
                    ? state?.configuration?.[section]?.metaDescription
                    : state?.metaDescription
                }
                handleChange={handleInputChange}
                multiline={true}
                inputSize={6}
                rowsMax={10}
                disabled={!isEditEnabled}
              />
              <GridContainer>
                <GridItem xs={12} sm={2}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('seo.indexPage')}
                  </FormLabel>
                </GridItem>
                <GridItem xs={12} sm={6}>
                  <LabeledCheckbox
                    onClick={handleToggle}
                    checked={
                      moduleConfiguration
                        ? state?.configuration?.[section]?.indexing
                        : state?.indexing
                    }
                    name="indexing"
                  />
                </GridItem>
              </GridContainer>
              <FormTextInput
                label={t('seo.openGraphTitle')}
                name="openGraphTitle"
                value={
                  moduleConfiguration
                    ? state?.configuration?.[section]?.openGraphTitle
                    : state?.openGraphTitle
                }
                handleChange={handleInputChange}
                inputSize={6}
                disabled={!isEditEnabled}
              />
              <FormTextInput
                label={t('seo.openGraphDesc')}
                name="openGraphDescription"
                value={
                  moduleConfiguration
                    ? state?.configuration?.[section]?.openGraphDescription
                    : state?.openGraphDescription
                }
                handleChange={handleInputChange}
                inputSize={6}
                rowsMax={10}
                disabled={!isEditEnabled}
              />
              <GridContainer className={classes.gridContainer}>
                <GridItem xs={12} sm={2} className={classes.gridItem}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('seo.openGraphPhoto')}
                  </FormLabel>
                </GridItem>
                <GridItem xs={12} sm={10} className={classes.uploadedWrapper}>
                  <FormImageUpload
                    stateName="image"
                    getImage={getImage}
                    removeImage={removeImage}
                    previewUrl={
                      moduleConfiguration
                        ? state?.configuration?.[section]?.imageUrl
                        : state?.imageUrl
                    }
                    isDisabled={!isEditEnabled}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
    </>
  );
};

export default SEOSettingsCms;
